import { DialogComponent } from '../../shared/ui/dialog/dialog.component';
import { ResetContentComponent } from '../../dialogs/reset-content/reset-content.component';
import { IContentGroup } from '../../store/models/content-group.model';
import { HttpClient } from '@angular/common/http';
import { IUserSessionData, UserType } from '../../store/models/user.model';
import { takeUntil } from 'rxjs/operators';
import { forkJoin, Subject } from 'rxjs';
import { SessionService } from '../../store/services/session.service';
import { ApiDataSource } from '../../datasources/api.datasrouce';
import {
  EFilterSize,
  EFilterType,
  ETableColumnType,
  ITableColumn,
  ITableFIlterGroup,
} from '../../store/models/table.model';
import { Store } from '@ngrx/store';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { IReducers } from 'app/store/reducers';
import { openDialog, openSnackbar } from 'app/store/actions/page.action';
import { InfoTipsComponent } from 'app/dialogs/info-tips/info-tips.component';
import { ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { environment } from '../../../environments/environment';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { ContentService } from '../../store/services/content.service';
import { ConfirmResetContentComponent } from '../../dialogs/confirm-reset-content/confirm-reset-content.component';
import { IContentItem } from '../../store/models/content.model';

@Component({
  selector: 'app-content-group-listing',
  templateUrl: './content-group-listing.component.html',
  styleUrls: ['./content-group-listing.component.scss'],
})
export class ContentGroupListingComponent implements OnInit, OnDestroy {
  tableReady: boolean;

  tableColumns: ITableColumn<IContentGroup>[];

  dataSource: ApiDataSource<IContentGroup>;

  tableFilterGroups: ITableFIlterGroup[] = [
    {
      filters: [
        {
          type: EFilterType.SEARCH,
          size: EFilterSize.HALF,
          items: [
            {
              id: 'search',
              label: 'Search',
              icon: 'search',
            },
          ],
        },
      ],
    },
  ];

  tableDataSourceParams = {
    page: 1,
    pageSize: 10,
    sortAttr: 'content_group_id',
    sortDir: 'desc',
  };

  iconPaperPlane = faPaperPlane;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private store: Store<IReducers>,
    private sessionService: SessionService,
    private http: HttpClient,
    private readonly contentService: ContentService,
  ) {
  }

  ngOnInit() {
    this.dataSource = new ApiDataSource<IContentGroup>(
      this.http,
      environment.apiUrl + '/v1/contents',
    );
    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe((sessionData: IUserSessionData) => {
        this.tableColumns = [
          {
            id: 'content_group',
            title: 'Group Name',
            sortable: true,
            asHtml: true,
            leftAdjusted: true,
            renderer: (row: IContentGroup) => {
              return `
                <div class='content-group-name'>
                  <h5>${row.content_group}</h5>
                  ${
                [UserType.MANAGER, UserType.MASTER].indexOf(
                  sessionData.user_type,
                ) !== -1
                  ? `<p>${
                    sessionData.user_type === UserType.MASTER
                      ? 'Account'
                      : 'Representative'
                  }: ${row.account_firstname}</p>`
                  : ''
              }
                </div>
              `;
            },
            class: 'contact-group',
          },
          {
            id: 'campaign_name',
            title: 'Associated Campaign',
            class: 'content-campaign-name',
            asHtml: true,
            leftAdjusted: true,
            renderer: (row: IContentGroup) => {
              return `<h5>${
                row.campaign_name ? row.campaign_name : 'No Campaign Associated'
              }</h5>`;
            },
          },
          {
            id: 'update',
            type: ETableColumnType.BUTTON,
            getButtonProperties: (element: IContentGroup) => {
              return {
                color: ButtonColor.BLUE,
                size: ButtonSize.SMALL,
                style: 'rounded',
                onClick: () => this.openUpdateContentDialog(element),
                text: 'Update',
                buttonDisabled: (e: IContentGroup) => !e.campaign_name,
              };
            },
          },
          {
            id: 'request',
            type: ETableColumnType.BUTTON,
            getButtonProperties: (element: IContentGroup) => {
              return {
                color: element.content_requested
                  ? ButtonColor.GREEN
                  : ButtonColor.BLUE,
                size: ButtonSize.SMALL,
                buttonDisabled: () => element.content_requested,
                style: 'filled',
                onClick: () => this.openRequestHelpDialog(element),
                observeApi: true,
                text: element.content_requested
                  ? 'Help requested has been sent'
                  : 'Request Help with Content',
              };
            },
          },
          {
            id: 'edit',
            type: ETableColumnType.BUTTON,
            buttonProperties: {
              color: ButtonColor.BLUE,
              size: ButtonSize.SMALL,
              text:
                sessionData.user_type === UserType.MANAGER ? 'View' : 'Edit',
              style: 'outline',
            },
            dynamicRoute: true,
            route: (row: IContentGroup) => {
              return [
                '/contents',
                row.content_group_id,
                'content',
                {
                  group_name: row.content_group,
                },
              ];
            },
          },
        ];
        this.tableReady = true;
      });
  }

  onInfoImageClick() {
    this.store.dispatch(
      openDialog({
        dialogData: {
          content: InfoTipsComponent,
          title: 'Tips',
          canClose: true,
          dialogMinWidth: '700px',
          componentData: {
            type: 'content_group',
          },
        },
      }),
    );
  }

  openRequestHelpDialog(element: IContentGroup): void {
    this.store.dispatch(
      openDialog({
        dialogData: {
          content:
            'Your content is very important to the success of your campaign. For content tips and examples, visit our Helpcenter from the support tab. Search "Content Tips". If you would like assistance from one of our content specialists, click Request Help with Content.',
          title: 'Request help with content',
          canClose: true,
          dialogMinWidth: '700px',
          buttons: [
            {
              color: ButtonColor.BLUE,
              size: ButtonSize.SMALL,
              text: 'Request Help with Content',
              onClick: (dialog) => this.contentService.requestContentHelp(element.content_group_id, element.content_group)
                .subscribe(() => {
                  this.store.dispatch(openSnackbar({
                    snackbarData: {
                      data: {
                        message: 'Help is requested for the content',
                        type: 'success',
                      },
                    },
                  }));
                  this.dataSource.refresh();
                  dialog.closeDialog();
                }, err => {
                  this.store.dispatch(openSnackbar({
                    snackbarData: {
                      data: {
                        message: err.error,
                        type: 'error',
                      },
                    },
                  }));
                  dialog.closeDialog();
                }),
            },
          ],
        },
      }),
    );
  }

  openUpdateContentDialog(element: IContentGroup): void {
    this.store.dispatch(
      openDialog({
        dialogData: {
          content: ResetContentComponent,
          title: 'Reset Content',
          canClose: true,
          dialogMinWidth: '700px',
          buttons: [
            {
              color: ButtonColor.GREEN,
              size: ButtonSize.SMALL,
              text: 'Reset Content',
              onClick: (dialog: DialogComponent) => {
                if (!dialog.dialogForm.valid) {
                  dialog.dialogForm.markAllAsTouched();
                  return;
                }

                const contentCode = dialog.dialogForm.get('code').value;

                const apiCalls = [
                  this.contentService.findContentByCode(contentCode, 'Email 1'),
                  this.contentService.findContentByCode(contentCode, 'Email 2'),
                  this.contentService.findContentByCode(contentCode, 'Email 3'),
                  this.contentService.findContentByCode(contentCode, 'Email 4'),
                ];

                forkJoin(apiCalls)
                  .subscribe(responses => {
                    for (const response of responses) {
                      if (!Object.keys(response).length) {
                        // get out if any of the API call do not return any data
                        this.store.dispatch(openSnackbar({
                          snackbarData: {
                            data: {
                              message: 'We could not find such a content code. Please, try again later',
                              type: 'error',
                            },
                          },
                        }));
                        dialog.closeDialog();
                        return;
                      }
                    }

                    this.store.dispatch(openDialog({
                      dialogData: {
                        content: ConfirmResetContentComponent,
                        componentData: {
                          contents: responses,
                        },
                        canClose: true,
                        title: 'Preview content',
                        dialogMinWidth: '700px',
                        buttons: [
                          {
                            text: 'Reset content',
                            color: ButtonColor.GREEN,
                            size: ButtonSize.NORMAL,
                            onClick: (confirmDialog) => {
                              this.contentService.getContentInfoByGroup(element.content_group_id)
                                .subscribe(res => {
                                  const targetItems: Pick<IContentItem, 'content_id' | 'content_template' | 'content_group_id'>[] = res.items.map(i => ({
                                    content_id: i.content_id,
                                    content_template: i.content_template,
                                    content_group_id: i.content_group_id,
                                  }));

                                  const updateApis = [];
                                  for (const item of targetItems) {
                                    const source = responses.find(s => s.content_template === item.content_template);


                                    updateApis.push(this.contentService.updateContent(item.content_id, {
                                      content_attention: source.content_attention,
                                      content_closing: source.content_closing,
                                      content_details: source.content_details,
                                      content_opening: source.content_opening,
                                      subject: source.subject,
                                      first_benefit: source.first_benefit,
                                      second_benefit: source.second_benefit,
                                      // @ts-ignore
                                      greeting: source.greeting,
                                      quick_answer: source.quick_answer,
                                      content_group_id: item.content_group_id,
                                    }));
                                  }

                                  forkJoin(updateApis)
                                    .subscribe(updateResult => {
                                      this.store.dispatch(openSnackbar({
                                        snackbarData: {
                                          data: {
                                            message: `Content Group ${element.content_group} successfully updated`,
                                            type: 'success',
                                          },
                                        },
                                      }));
                                      confirmDialog.closeDialog();
                                      dialog.closeDialog();
                                    });
                                }, error => {
                                  this.store.dispatch(openSnackbar({
                                    snackbarData: {
                                      data: {
                                        message: 'We could not find such a content code. Please, try again later',
                                        type: 'error',
                                      },
                                    },
                                  }));
                                  dialog.closeDialog();
                                  confirmDialog.closeDialog();
                                });
                            },
                          },
                        ],
                      },
                    }));

                    dialog.closeDialog();


                  }, error => {
                    this.store.dispatch(openSnackbar({
                      snackbarData: {
                        data: {
                          message: 'We could not find such a content code. Please, try again later',
                          type: 'error',
                        },
                      },
                    }));
                    dialog.closeDialog();
                  });
                console.log('dialog submit', contentCode);
              },
            },
          ],
        },
      }),
    );
  }

  ngOnDestroy() {
    this.destroy$.next(false);
  }
}
